import React from 'react';
import { useHistory } from 'react-router-dom';
import { faTicket } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import WarningModal from 'components/modals/WarningModal';
import { saveData } from 'services/api/saveData';
import { useActiveFeature } from 'services/permissions/permissionChecks';

/*
 * useSubcontractorDayPassCheck.tsx
 * Hook which checks before saving whether the environment has enough
 * subcontractor day passes for a planned change, and which turns the
 * day pass error of the backend into a warning modal with a button to
 * the ordering page. Users without the subcontractor role never need
 * passes, so callers may always ask without knowing the role.
 */

// The route of the subcontractors settings page where day passes are ordered
export const SUBCONTRACTOR_SETTINGS_ROUTE = '/settings/general/subcontractors';

// The error code the backend returns when the day passes are insufficient
export const DAY_PASS_ERROR_CODE = 'no_subcontractor_day_passes';

// One planned change to check, a user with explicit dates or a datetime range
export type DayPassCheckItem = {
    user_hash?: string | null;
    user_id?: number | null;
    resource_id?: string | number | null;
    dates?: string[];
    start_datetime?: string | null;
    end_datetime?: string | null;
    timezone?: string;
};

// The result of the check endpoint
export interface DayPassCheckResult {
    feature_active: boolean;
    needed: number;
    existing: number;
    balance: number;
    sufficient: boolean;
    subcontractor_user_ids: number[];
}

// Checks whether an error is the day pass error of the backend
export const isDayPassError = (error: any): boolean => {
    return error?.response?.data?.error_code === DAY_PASS_ERROR_CODE;
};

export const useSubcontractorDayPassCheck = () => {
    const { initializeModal } = useModal();
    const history = useHistory();
    const featureActive = useActiveFeature('subcontractors');

    // Open the warning modal with a button to the ordering page
    const openInsufficientModal = (needed: number, balance: number) => {
        initializeModal(
            <WarningModal
                type='info'
                icon={faTicket}
                header='subcontractor.day_pass.insufficient_header'
                message='subcontractor.day_pass.insufficient_message'
                messageValues={{ needed: String(needed), balance: String(Math.max(balance, 0)) }}
                buttonLabel='subcontractor.day_pass.order_button'
                onApproval={() => history.push(SUBCONTRACTOR_SETTINGS_ROUTE)}
            />,
            { modalSize: 'extra-small' }
        );
    };

    // Resolves true when saving may proceed, false when the modal was shown
    const checkDayPasses = async (items: DayPassCheckItem[]): Promise<boolean> => {
        if (items.length === 0) return true;

        try {
            // Ask the backend how many new passes the change needs
            const response = await saveData({ apiUrl: 'check_subcontractor_day_passes', method: 'post', data: { items } });
            const result = response?.data as DayPassCheckResult | undefined;
            if (!result || result.sufficient) return true;

            // Show the modal and block the save
            openInsufficientModal(result.needed, result.balance);
            return false;
        } catch (error) {
            // Never block saving on a failed pre check, the backend enforces anyway
            return true;
        }
    };

    // Returns true when the error was the day pass error and the modal was shown
    const handleDayPassError = (error: any): boolean => {
        if (!isDayPassError(error)) return false;
        const data = error.response.data;
        openInsufficientModal(Number(data.needed) || 0, Number(data.balance) || 0);
        return true;
    };

    return { checkDayPasses, handleDayPassError, openInsufficientModal, featureActive };
};
